import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Avatar,
  Tooltip,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import groupChatService from "API/groupChatService";
import signalRManager from "services/signalRManager";
import { getFullUrl } from "config/api";
import { decryptText } from "config/encryption";
import safeToast from "utils/safeToast";

const AdminGroupChatDialog = ({ open, onClose }) => {
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [joiningGroupId, setJoiningGroupId] = useState(null);
  const [joinedGroupId, setJoinedGroupId] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchingMembersForAdd, setSearchingMembersForAdd] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [uploadingGroupImage, setUploadingGroupImage] = useState(false);

  const [createMode, setCreateMode] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const data = await groupChatService.getGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      safeToast.error("Failed to load groups");
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  const loadHistory = useCallback(async (groupId, page = 1, append = false) => {
    setLoadingHistory(true);
    try {
      const res = await groupChatService.getHistory(groupId, page, 50);
      const msgs = res?.messages || [];
      if (append) {
        setHistory((prev) => [...msgs, ...prev]);
      } else {
        setHistory(msgs);
      }
      setTotalCount(res?.totalCount || 0);
      setPageNumber(page);
      setTimeout(() => scrollToBottom(), 50);
    } catch (e) {
      console.error(e);
      safeToast.error("Failed to load group history");
    } finally {
      setLoadingHistory(false);
    }
  }, [scrollToBottom]);

  // When dialog opens: ensure SignalR running, load groups
  useEffect(() => {
    if (!open) return;
    (async () => {
      await signalRManager.startConnection();
      await loadGroups();
    })();
  }, [open, loadGroups]);

  useEffect(() => {
    if (!open) return;
    try {
      const adminSession =
        localStorage.getItem("adminLogsessiondetails") ||
        localStorage.getItem("adminsessiondetails");
      if (!adminSession) return;
      const decrypted = decryptText(adminSession);
      const parsed = JSON.parse(decrypted);
      const sid = parsed?.StaffId ?? parsed?.staffId ?? null;
      setCurrentStaffId(sid ? Number(sid) : null);
    } catch (e) {
      console.error("Failed to read admin session:", e);
      setCurrentStaffId(null);
    }
  }, [open]);

  // Track online users to show WhatsApp-like status in Group Info.
  useEffect(() => {
    if (!open) return;
    const handleOnlineUsersUpdated = (users) => {
      setOnlineUsers(Array.isArray(users) ? users : []);
    };
    signalRManager.on("onOnlineUsersUpdated", handleOnlineUsersUpdated);
    return () => signalRManager.off("onOnlineUsersUpdated", handleOnlineUsersUpdated);
  }, [open]);

  // Subscribe to incoming group messages
  useEffect(() => {
    if (!open) return;
    const handler = (msg) => {
      if (!msg || !selectedGroup?.groupId) return;
      if (msg.groupId !== selectedGroup.groupId) return;
      setHistory((prev) => {
        if (prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
      setTimeout(() => scrollToBottom(), 50);
    };
    signalRManager.on("onGroupMessage", handler);
    return () => signalRManager.off("onGroupMessage", handler);
  }, [open, selectedGroup, scrollToBottom]);

  // When selecting a group: join room + load history
  useEffect(() => {
    if (!open) return;
    if (!selectedGroup?.groupId) return;
    (async () => {
      setJoiningGroupId(selectedGroup.groupId);
      const joined = await signalRManager.joinGroup(selectedGroup.groupId);
      setJoinedGroupId(joined ? selectedGroup.groupId : null);
      await loadHistory(selectedGroup.groupId, 1, false);
      setJoiningGroupId(null);
    })();
  }, [open, selectedGroup, loadHistory]);

  const canLoadOlder = useMemo(() => {
    return totalCount > history.length;
  }, [totalCount, history.length]);

  const loadOlder = useCallback(async () => {
    if (!selectedGroup?.groupId) return;
    const next = pageNumber + 1;
    await loadHistory(selectedGroup.groupId, next, true);
  }, [selectedGroup, pageNumber, loadHistory]);

  const handleSend = useCallback(async () => {
    if (!selectedGroup?.groupId) return;
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      let ok = false;
      if (joinedGroupId === selectedGroup.groupId) {
        ok = await signalRManager.sendGroupMessage(selectedGroup.groupId, text, null, null);
      }
      if (!ok) {
        // Fallback: REST send (also broadcasts via hub server-side)
        const saved = await groupChatService.sendMessage(selectedGroup.groupId, text, null, null);
        if (saved?.messageId) {
          setHistory((prev) => {
            if (prev.some((m) => m.messageId === saved.messageId)) return prev;
            return [...prev, saved];
          });
          setTimeout(() => scrollToBottom(), 50);
        }
      }
      setMessage("");
    } catch (e) {
      console.error(e);
      safeToast.error("Failed to send");
    } finally {
      setSending(false);
    }
  }, [selectedGroup, message, sending, joinedGroupId, scrollToBottom]);

  const handleSearchUsers = useCallback(async (q) => {
    const query = (q || "").trim();
    setUserSearch(query);
    if (query.length < 2) {
      setUserResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await groupChatService.searchUsers(query, 20);
      setUserResults(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setUserResults([]);
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  const toggleMember = useCallback((u) => {
    const key = u.isStaff ? `S:${u.staffId}` : `U:${u.studentId}`;
    setSelectedMembers((prev) => {
      const exists = prev.some((x) => (x.isStaff ? `S:${x.staffId}` : `U:${x.studentId}`) === key);
      if (exists) return prev.filter((x) => (x.isStaff ? `S:${x.staffId}` : `U:${x.studentId}`) !== key);
      return [...prev, u];
    });
  }, []);

  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const members = selectedMembers.map((m) => ({
        isStaff: !!m.isStaff,
        studentId: m.studentId || null,
        staffId: m.staffId || null,
      }));
      const group = await groupChatService.createGroup(name, members);
      safeToast.success("Group created");
      setCreateMode(false);
      setNewGroupName("");
      setUserSearch("");
      setUserResults([]);
      setSelectedMembers([]);
      await loadGroups();
      if (group?.groupId) setSelectedGroup(group);
    } catch (e) {
      console.error(e);
      safeToast.error(e?.response?.data?.error || "Failed to create group");
    }
  }, [newGroupName, selectedMembers, loadGroups]);

  const isMemberOnline = useCallback((member) => {
    if (!member) return false;
    return onlineUsers.some((u) => {
      const onlineIsStaff = !!u.isStaff;
      const onlineStudentId = u.studentId ?? u.StudentId ?? null;
      const onlineStaffId = u.staffId ?? u.StaffId ?? null;
      if (member.isStaff) {
        return onlineIsStaff && Number(onlineStaffId) === Number(member.staffId);
      }
      return !onlineIsStaff && Number(onlineStudentId) === Number(member.studentId);
    });
  }, [onlineUsers]);

  const handleOpenGroupInfo = useCallback(async () => {
    if (!selectedGroup?.groupId) return;
    setLoadingGroupMembers(true);
    setGroupInfoOpen(true);
    try {
      const data = await groupChatService.getMembers(selectedGroup.groupId);
      setGroupMembers(Array.isArray(data) ? data : []);
      setMemberSearchQuery("");
      setMemberSearchResults([]);
      setMembersToAdd([]);
    } catch (e) {
      console.error(e);
      setGroupMembers([]);
      safeToast.error("Failed to load group members");
    } finally {
      setLoadingGroupMembers(false);
      setHeaderMenuAnchor(null);
    }
  }, [selectedGroup]);

  const isCurrentUserOwner =
    !!selectedGroup &&
    currentStaffId != null &&
    Number(selectedGroup.createdByStaffId) === Number(currentStaffId);

  const searchMembersToAdd = useCallback(async (q) => {
    const query = (q || "").trim();
    setMemberSearchQuery(query);
    if (query.length < 2) {
      setMemberSearchResults([]);
      return;
    }
    setSearchingMembersForAdd(true);
    try {
      const res = await groupChatService.searchUsers(query, 20);
      const existing = new Set(
        (groupMembers || []).map((m) => `${m.isStaff ? "S" : "U"}:${m.isStaff ? m.staffId : m.studentId}`)
      );
      const filtered = (Array.isArray(res) ? res : []).filter((u) => {
        const key = `${u.isStaff ? "S" : "U"}:${u.isStaff ? u.staffId : u.studentId}`;
        return !existing.has(key);
      });
      setMemberSearchResults(filtered);
    } catch (e) {
      console.error(e);
      setMemberSearchResults([]);
    } finally {
      setSearchingMembersForAdd(false);
    }
  }, [groupMembers]);

  const toggleMemberToAdd = useCallback((u) => {
    const key = `${u.isStaff ? "S" : "U"}:${u.isStaff ? u.staffId : u.studentId}`;
    setMembersToAdd((prev) => {
      const has = prev.some((x) => `${x.isStaff ? "S" : "U"}:${x.isStaff ? x.staffId : x.studentId}` === key);
      if (has) {
        return prev.filter((x) => `${x.isStaff ? "S" : "U"}:${x.isStaff ? x.staffId : x.studentId}` !== key);
      }
      return [...prev, u];
    });
  }, []);

  const handleAddMembersToGroup = useCallback(async () => {
    if (!selectedGroup?.groupId || membersToAdd.length === 0) return;
    if (!isCurrentUserOwner) {
      safeToast.error("Only group owner can add members");
      return;
    }
    setAddingMembers(true);
    try {
      const payload = membersToAdd.map((m) => ({
        isStaff: !!m.isStaff,
        studentId: m.studentId || null,
        staffId: m.staffId || null,
      }));
      await groupChatService.addMembers(selectedGroup.groupId, payload);
      safeToast.success("Members added successfully");
      const [membersData, groupsData] = await Promise.all([
        groupChatService.getMembers(selectedGroup.groupId),
        groupChatService.getGroups(),
      ]);
      setGroupMembers(Array.isArray(membersData) ? membersData : []);
      if (Array.isArray(groupsData)) {
        setGroups(groupsData);
        const refreshed = groupsData.find((g) => g.groupId === selectedGroup.groupId);
        if (refreshed) setSelectedGroup(refreshed);
      }
      setMemberSearchQuery("");
      setMemberSearchResults([]);
      setMembersToAdd([]);
    } catch (e) {
      console.error(e);
      safeToast.error(e?.response?.data?.error || "Failed to add members");
    } finally {
      setAddingMembers(false);
    }
  }, [selectedGroup, membersToAdd, isCurrentUserOwner]);

  const handleUploadGroupImage = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !selectedGroup?.groupId) return;
    if (!isCurrentUserOwner) {
      safeToast.error("Only group owner can upload group image");
      return;
    }
    setUploadingGroupImage(true);
    try {
      const res = await groupChatService.uploadGroupImage(selectedGroup.groupId, file);
      const uploadedImageUrl = res?.imageUrl || null;
      if (uploadedImageUrl) {
        setSelectedGroup((prev) => (prev ? { ...prev, groupImageUrl: uploadedImageUrl } : prev));
      }
      const groupsData = await groupChatService.getGroups();
      if (Array.isArray(groupsData)) {
        setGroups(groupsData);
        const refreshed = groupsData.find((g) => g.groupId === selectedGroup.groupId);
        if (refreshed) setSelectedGroup(refreshed);
      }
      safeToast.success("Group profile image updated");
    } catch (e) {
      console.error(e);
      safeToast.error(e?.response?.data?.error || "Failed to upload group image");
    } finally {
      setUploadingGroupImage(false);
      if (event?.target) event.target.value = "";
    }
  }, [selectedGroup, isCurrentUserOwner]);

  const getAvatarUrl = (profileImageUrl) => {
    if (!profileImageUrl) return null;
    return getFullUrl(profileImageUrl);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          "& .MuiTypography-root": {
            fontSize: "0.82rem",
          },
          "& .MuiButton-root": {
            fontSize: "0.78rem",
          },
          "& .MuiChip-label": {
            fontSize: "0.72rem",
          },
          "& .MuiInputBase-input": {
            fontSize: "0.8rem",
          },
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupIcon />
          <Typography variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 700 }}>Group Chat</Typography>
          <Chip size="small" label={`${groups.length} groups`} />
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
            disabled={!selectedGroup}
          >
            <MoreVertIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "100%", display: "flex" }}>
        {/* Left: Groups */}
        <Box sx={{ width: 320, borderRight: "1px solid", borderColor: "divider", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 1.5, display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant={createMode ? "contained" : "outlined"}
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateMode((v) => !v)}
              sx={{
                ...(createMode
                  ? {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": { bgcolor: "primary.dark" },
                    }
                  : {
                      color: "primary.main",
                      borderColor: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        color: "primary.dark",
                        bgcolor: "primary.lighter",
                      },
                    }),
                "&.Mui-disabled": {
                  color: "primary.main",
                  borderColor: "rgba(25, 118, 210, 0.35)",
                  bgcolor: "rgba(25, 118, 210, 0.12)",
                },
              }}
            >
              {createMode ? "Close" : "Create"}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={loadGroups}
              disabled={loadingGroups}
              sx={{
                color: "primary.main",
                "&:hover": {
                  color: "primary.dark",
                  bgcolor: "primary.lighter",
                },
                "&.Mui-disabled": {
                  color: "primary.main",
                  opacity: 0.6,
                },
              }}
            >
              Refresh
            </Button>
          </Box>

          {createMode && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <TextField
                label="Group name"
                size="small"
                fullWidth
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
              <Box sx={{ mt: 1 }}>
                <TextField
                  label="Add members (search)"
                  size="small"
                  fullWidth
                  value={userSearch}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                />
              </Box>

              <Box sx={{ mt: 1, maxHeight: 160, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                {searchingUsers ? (
                  <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : userResults.length === 0 ? (
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Type at least 2 characters to search students/staff.
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {userResults.map((u) => {
                      const key = u.isStaff ? `S:${u.staffId}` : `U:${u.studentId}`;
                      const selected = selectedMembers.some((x) => (x.isStaff ? `S:${x.staffId}` : `U:${x.studentId}`) === key);
                      return (
                        <ListItemButton key={key} onClick={() => toggleMember(u)} selected={selected}>
                          <Avatar src={getAvatarUrl(u.profileImageUrl)} sx={{ width: 28, height: 28, mr: 1 }}>
                            {(u.displayName || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">{u.displayName}</Typography>
                                <Chip size="small" label={u.isStaff ? "Staff" : "Student"} />
                              </Box>
                            }
                            secondary={u.userName ? `@${u.userName}` : ""}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>

              {selectedMembers.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedMembers.map((m) => (
                    <Chip
                      key={m.isStaff ? `S:${m.staffId}` : `U:${m.studentId}`}
                      label={m.displayName}
                      onDelete={() => toggleMember(m)}
                      size="small"
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setCreateMode(false)}
                  sx={{
                    color: "primary.main",
                    borderColor: "primary.main",
                    "&:hover": {
                      borderColor: "primary.dark",
                      color: "primary.dark",
                      bgcolor: "primary.lighter",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleCreateGroup}
                  disabled={!newGroupName.trim()}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    "&.Mui-disabled": {
                      color: "primary.main",
                      bgcolor: "rgba(25, 118, 210, 0.12)",
                    },
                  }}
                >
                  Create
                </Button>
              </Box>
            </Box>
          )}

          <Divider />

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {loadingGroups ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={22} />
              </Box>
            ) : groups.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No groups yet. Create one to start.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {groups.map((g) => (
                  <ListItemButton
                    key={g.groupId}
                    selected={selectedGroup?.groupId === g.groupId}
                    onClick={() => setSelectedGroup(g)}
                  >
                    <Avatar src={getAvatarUrl(g.groupImageUrl)} sx={{ width: 32, height: 32, mr: 1 }}>
                      {(g.groupName || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: "0.82rem" }}>
                            {g.groupName}
                          </Typography>
                          <Chip size="small" label={g.memberCount || 0} />
                        </Box>
                      }
                      secondary={g.lastMessagePreview ? g.lastMessagePreview : " "}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Right: Messages */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center" gap={1}>
              {!!selectedGroup && (
                <Avatar src={getAvatarUrl(selectedGroup.groupImageUrl)} sx={{ width: 34, height: 34 }}>
                  {(selectedGroup.groupName || "?").charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
                {selectedGroup?.groupName || "Select a group"}
              </Typography>
              {selectedGroup?.memberCount != null && (
                <Typography variant="caption" color="text.secondary">
                  {selectedGroup.memberCount} members
                </Typography>
              )}
              </Box>
            </Box>
            {!!selectedGroup && (
                <Tooltip title="This chat is live via SignalR">
                  <Chip size="small" color="success" label="Live" />
                </Tooltip>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "rgba(0,0,0,0.02)" }}>
            {!selectedGroup ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Pick a group on the left to view messages.
                </Typography>
              </Box>
            ) : loadingHistory ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <>
                {canLoadOlder && (
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                    <Button size="small" onClick={loadOlder}>
                      Load older messages
                    </Button>
                  </Box>
                )}

                {history.map((m) => (
                  <Box key={m.messageId} sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Avatar src={getAvatarUrl(m.profileImageUrl)} sx={{ width: 28, height: 28 }}>
                        {(m.displayName || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Paper sx={{ p: 1, flex: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.74rem" }}>
                            {m.displayName || m.userName || "User"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {m.sentDateTime ? new Date(m.sentDateTime).toLocaleTimeString() : ""}
                          </Typography>
                        </Box>
                        {m.imageUrl && (
                          <Box sx={{ mt: 1 }}>
                            <img
                              src={getFullUrl(m.imageUrl)}
                              alt="shared"
                              style={{ maxWidth: "100%", maxHeight: 260, borderRadius: 6, cursor: "pointer" }}
                              onClick={() => window.open(getFullUrl(m.imageUrl), "_blank")}
                            />
                          </Box>
                        )}
                        {m.audioUrl && (
                          <Box sx={{ mt: 1 }}>
                            <audio controls src={getFullUrl(m.audioUrl)} style={{ width: "100%" }} />
                          </Box>
                        )}
                        {m.message && (
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
                            {m.message}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider", display: "flex", gap: 1 }}>
            <TextField
              placeholder={selectedGroup ? "Type a message..." : "Select a group to chat"}
              size="small"
              fullWidth
              value={message}
              disabled={!selectedGroup}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={4}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={
                !selectedGroup ||
                !message.trim() ||
                sending
              }
            >
              {sending ? <CircularProgress size={18} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={() => setHeaderMenuAnchor(null)}
      >
        <MenuItem onClick={handleOpenGroupInfo}>
          <InfoOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Group info
        </MenuItem>
      </Menu>

      <Dialog open={groupInfoOpen} onClose={() => setGroupInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Group info
        </DialogTitle>
        <DialogContent>
          {!selectedGroup ? null : (
            <Box display="flex" alignItems="center" gap={1.2}>
              <Avatar src={getAvatarUrl(selectedGroup.groupImageUrl)} sx={{ width: 42, height: 42 }}>
                {(selectedGroup.groupName || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Box>
              <Typography variant="h6" sx={{ fontSize: "1rem", mb: 0.25 }}>
                {selectedGroup.groupName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedGroup.memberCount || groupMembers.length || 0} members
              </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box mb={1.5}>
            <Typography variant="body2" fontWeight={700}>
              Group profile image
            </Typography>
            {!isCurrentUserOwner ? (
              <Typography variant="caption" color="text.secondary">
                Only group owner can upload group image.
              </Typography>
            ) : (
              <Button
                component="label"
                size="small"
                variant="outlined"
                disabled={uploadingGroupImage}
                sx={{ mt: 0.8, color: "primary.main", borderColor: "primary.main" }}
              >
                {uploadingGroupImage ? "Uploading..." : "Upload image"}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={handleUploadGroupImage}
                />
              </Button>
            )}
          </Box>

          <Box mb={1.5}>
            <Typography variant="body2" fontWeight={700}>
              Add members
            </Typography>
            {!isCurrentUserOwner ? (
              <Typography variant="caption" color="text.secondary">
                Only group owner can add members.
              </Typography> 
            ) : (
              <>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search users to add"
                  value={memberSearchQuery}
                  onChange={(e) => searchMembersToAdd(e.target.value)}
                />
                <Box sx={{ mt: 0.8, maxHeight: 120, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  {searchingMembersForAdd ? (
                    <Box py={1.2} display="flex" justifyContent="center">
                      <CircularProgress size={18} />
                    </Box>
                  ) : memberSearchResults.length === 0 ? (
                    <Box p={1}>
                      <Typography variant="caption" color="text.secondary">
                        Type at least 2 characters.
                      </Typography>
                    </Box>
                  ) : (
                    <List dense disablePadding>
                      {memberSearchResults.map((u) => {
                        const key = `${u.isStaff ? "S" : "U"}:${u.isStaff ? u.staffId : u.studentId}`;
                        const selected = membersToAdd.some(
                          (x) =>
                            `${x.isStaff ? "S" : "U"}:${x.isStaff ? x.staffId : x.studentId}` === key
                        );
                        return (
                          <ListItemButton key={key} selected={selected} onClick={() => toggleMemberToAdd(u)}>
                            <Avatar src={getAvatarUrl(u.profileImageUrl)} sx={{ width: 24, height: 24, mr: 1 }}>
                              {(u.displayName || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <ListItemText
                              primary={<Typography variant="body2">{u.displayName}</Typography>}
                              secondary={u.isStaff ? "Staff" : "Student"}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </Box>
                {membersToAdd.length > 0 && (
                  <Box mt={0.8} display="flex" flexWrap="wrap" gap={0.5}>
                    {membersToAdd.map((m) => (
                      <Chip
                        key={`${m.isStaff ? "S" : "U"}:${m.isStaff ? m.staffId : m.studentId}`}
                        label={m.displayName}
                        size="small"
                        onDelete={() => toggleMemberToAdd(m)}
                      />
                    ))}
                  </Box>
                )}
                <Box mt={0.8} display="flex" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    disabled={membersToAdd.length === 0 || addingMembers}
                    onClick={handleAddMembersToGroup}
                    sx={{
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                      "&.Mui-disabled": {
                        color: "primary.main",
                        bgcolor: "rgba(25, 118, 210, 0.12)",
                      },
                    }}
                  >
                    {addingMembers ? "Adding..." : "Add members"}
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {loadingGroupMembers ? (
            <Box py={2} display="flex" justifyContent="center">
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box>
              {groupMembers.map((member) => {
                const isOwner = !!member.isStaff && Number(member.staffId) === Number(selectedGroup?.createdByStaffId);
                const online = isMemberOnline(member);
                return (
                  <Box key={`${member.isStaff ? "S" : "U"}-${member.staffId || member.studentId}`} display="flex" alignItems="center" py={0.75}>
                    <Avatar src={getAvatarUrl(member.profileImageUrl)} sx={{ width: 32, height: 32, mr: 1.2 }}>
                      {(member.displayName || member.userName || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {member.displayName || member.userName || "Member"}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.6}>
                        <FiberManualRecordIcon sx={{ fontSize: 9, color: online ? "success.main" : "text.disabled" }} />
                        <Typography variant="caption" color="text.secondary">
                          {online ? "Online" : "Offline"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • {isOwner ? "Owner" : member.isStaff ? "Admin" : "Member"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

AdminGroupChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminGroupChatDialog;

